import superagent from "superagent";
import { identity, is, isEmpty, isNil } from "ramda";
import httpInterceptActions from "actions/httpInterceptActions";
import { getToken } from "actions/loginActions";
import httpIntercept from "./httpIntercept";
import { store } from "../App";
import api from "./flexapi";
import { isMobileWebView, getCookieValue } from "cxa-utils";

/**
 * Partial content
 * @see {@link https://developer.mozilla.org/ko/docs/Web/HTTP/Status/206}
 */
const httpIntercepter = (err, response) => {
  const { status } = response;

  // handle errors
  if (status > 206) {
    const apiErrors = httpInterceptActions.apiErrors(response, status);

    apiErrors(store.dispatch, store.getState());
  }
};

/**
 * Finally
 */
const end = (resolve, reject) => (err, response) => {
  if (err) {
    return reject(err);
  }

  resolve(response);
};

/**
 * Create request, XMLHttpRequest
 * @param  {Object?} [options={}]
 * @return {Promise}
 */
function createRequest(options = {}) {
  const {
    method = "GET",
    authType = "", // no set - cookie, 'bearer' - Bearer token, 'none' - No auth
    url = "",
    query,
    body,
    accept,
    file,
    handlers,
    contentType,
    responseType,
    enhance = identity,
  } = options;
  let agent = superagent.agent();

  const selectedLanguageItem = localStorage.getItem("selectedLanguage");
  let selectedLanguage = { id: "enus" };

  try {
    if (selectedLanguageItem) {
      selectedLanguage = JSON.parse(selectedLanguageItem);
    }
  } catch (error) {
    console.warn("Failed to parse selectedLanguage from localStorage", error);
  }

  if (isEmpty(url)) {
    throw new Error("Please, include URL!");
  }

  const { userAgent } = window.navigator;

  // NOTE: Authorization type
  const nextAuthType = isMobileWebView(userAgent) ? "bearer" : authType.toLowerCase();

  if (nextAuthType === "bearer") {
    const bearerHeader = { Authorization: `Bearer ${getToken()}` };

    agent = agent.set(bearerHeader);
  } else if (nextAuthType !== "none") {
    // cookie
    agent = agent.withCredentials();
  }

  const csrfToken = getCookieValue(`pp-secure-token`);

  if (csrfToken && method && method !== "GET") {
    agent = agent.set({ "XSRF-TOKEN": csrfToken });
  }

  // NOTE: Method
  const nextMethod = method.toLowerCase();
  agent = agent[nextMethod](url);

  // NOTE: Event listeners
  if (is(Object, handlers)) {
    Object.keys(handlers).forEach(eventName => {
      const eventListener = handlers[eventName];

      agent = agent.on(eventName, eventListener);
    });
  }

  // NOTE: Query
  if (is(Object, query)) {
    agent = agent.query(query);
  }

  // NOTE: Message body(using send by Superagent)
  if (!isEmpty(body) && !isNil(body)) {
    agent = agent.send(body);
  }

  // NOTE: Accept type
  if (is(String, accept)) {
    agent = agent.accept(accept);
  }

  // NOTE: Attachment
  if (!isEmpty(file) && !isNil(file)) {
    agent = agent.attach("fileData", file);
  }

  // NOTE: Content-Type
  if (is(String, contentType)) {
    agent = agent.set("Content-Type", contentType);
  }

  // NOTE: responseType
  if (is(String, responseType)) {
    agent = agent.responseType(responseType);
  }

  agent = agent.set("Language", selectedLanguage.id);

  return new Promise((resolve, reject) =>
    enhance(agent)
      .use(httpIntercept(httpIntercepter))
      // .timeout({ response: 10000, deadline: 60000 })
      .end(end(resolve, reject))
  );
}

/**
 * ------------------
 *     NOTE: API
 * ------------------
 */

const get = (url, data, contentType = "application/json", responseType) =>
  createRequest({
    query: data,
    url,
    contentType,
    responseType,
  });

const getWithoutBearer = (url, data, contentType = "application/json", responseType) =>
  createRequest({
    authType: "none",
    query: data,
    url,
    contentType,
    responseType,
  });

const post = (url, data, { contentType = "application/json", accept = "application/json" } = {}) =>
  createRequest({
    method: "POST",
    body: data,
    url,
    accept,
    contentType,
  });

const put = (url, data, { contentType = "application/json", accept = "application/json" } = {}) =>
  createRequest({
    method: "PUT",
    body: data,
    url,
    accept,
    contentType,
  });

const deleteFile = (
  url,
  data,
  { contentType = "application/json", accept = "application/json" } = {}
) =>
  createRequest({
    method: "DELETE",
    body: data,
    url,
    accept,
    contentType,
  });

const postDigitalContent = (url, file, enhance) =>
  createRequest({
    method: "POST",
    url,
    file,
    enhance,
  });

const postHEICContent = (url, file, enhance) =>
  createRequest({
    method: "POST",
    url,
    file,
    enhance,
    responseType: "blob",
  });

const postFiles = (url, files, progressCallback) =>
  createRequest({
    method: "POST",
    contentType: "application/octet-stream",
    body: files,
    handlers: {
      progress: progressCallback,
    },
  });

export {
  get,
  post,
  put,
  deleteFile,
  postDigitalContent,
  postHEICContent,
  postFiles,
  getToken,
  getWithoutBearer,
};
export default api;



// WEBPACK FOOTER //
// ./api/index.js